import { useMemo, useState } from "react";
import { requirements, type Requirement, type RequirementStatus, type RequirementPriority } from "./requirements";

const STATUS_TONE: Record<RequirementStatus, string> = {
  implemented: "secondary",
  partial: "primary",
  planned: "tertiary",
};

const STATUS_LABEL: Record<RequirementStatus, string> = {
  implemented: "Implemented",
  partial: "Partial",
  planned: "Planned",
};

export function AboutRequirements() {
  const [q, setQ] = useState("");
  const [status, setStatus] = useState<RequirementStatus | "all">("all");
  const [priority, setPriority] = useState<RequirementPriority | "all">("all");
  const [area, setArea] = useState<string>("all");
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const areas = useMemo(() => Array.from(new Set(requirements.map((r) => r.area))), []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return requirements.filter((r) => {
      if (status !== "all" && r.status !== status) return false;
      if (priority !== "all" && r.priority !== priority) return false;
      if (area !== "all" && r.area !== area) return false;
      if (!needle) return true;
      return (
        r.id.toLowerCase().includes(needle) ||
        r.title.toLowerCase().includes(needle) ||
        r.description.toLowerCase().includes(needle)
      );
    });
  }, [q, status, priority, area]);

  return (
    <div style={{ display: "grid", gap: 16 }}>
      <div className="card" style={{ padding: 16, display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center" }}>
        <input
          type="search"
          placeholder="Search requirements…"
          aria-label="Search requirements"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          style={{
            flex: "1 1 200px", minWidth: 180, height: 36, padding: "0 12px",
            borderRadius: "var(--r-pill)", border: "1px solid var(--md-outline-var)",
            background: "var(--md-sc-lowest)", color: "var(--md-on-surface)",
            font: "var(--t-body-md)",
          }}
        />
        <ChipSelect label="Status" value={status} options={[
          { v: "all", l: "All status" },
          { v: "implemented", l: "Implemented" },
          { v: "partial", l: "Partial" },
          { v: "planned", l: "Planned" },
        ]} onChange={(v) => setStatus(v as RequirementStatus | "all")} />
        <ChipSelect label="Priority" value={priority} options={[
          { v: "all", l: "All priority" },
          { v: "P0" }, { v: "P1" }, { v: "P2" },
        ]} onChange={(v) => setPriority(v as RequirementPriority | "all")} />
        <ChipSelect label="Area" value={area} options={[{ v: "all", l: "All areas" }, ...areas.map((a) => ({ v: a }))]} onChange={setArea} />
      </div>

      <div style={{ display: "grid", gap: 8 }}>
        {filtered.length === 0 && (
          <div className="card" style={{ padding: 28, textAlign: "center", color: "var(--md-on-surface-var)" }}>
            No requirements match those filters.
          </div>
        )}
        {filtered.map((r) => (
          <RequirementRow key={r.id} r={r} open={!!expanded[r.id]} onToggle={() => setExpanded((s) => ({ ...s, [r.id]: !s[r.id] }))} />
        ))}
      </div>
    </div>
  );
}

function RequirementRow({ r, open, onToggle }: { r: Requirement; open: boolean; onToggle: () => void }) {
  const tone = STATUS_TONE[r.status];
  return (
    <div className="card lex-req-row">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        aria-controls={`req-${r.id}`}
        style={{
          all: "unset", cursor: "pointer", padding: 16,
          display: "grid", gridTemplateColumns: "auto 1fr auto auto", gap: 12, alignItems: "center",
        }}
      >
        <span style={{ font: "var(--t-mono-sm)", color: "var(--md-on-surface-var)" }}>{r.id}</span>
        <span style={{ font: "var(--t-title-sm)" }}>{r.title}</span>
        <span className="lex-chip" data-size="sm">{r.priority}</span>
        <span className="lex-chip" data-size="sm" data-tone={tone} aria-label={`Status: ${STATUS_LABEL[r.status]}`}>
          {STATUS_LABEL[r.status]}
        </span>
      </button>
      {open && (
        <div id={`req-${r.id}`} style={{ padding: "0 16px 16px 16px", display: "grid", gap: 10 }}>
          <p style={{ font: "var(--t-body-md)", color: "var(--md-on-surface-var)", margin: 0 }}>{r.description}</p>
          <div>
            <div style={{ font: "var(--t-label-md)", color: "var(--md-on-surface-var)" }}>Acceptance</div>
            <ul style={{ margin: "6px 0 0 0", paddingLeft: 20, font: "var(--t-body-md)" }}>
              {r.acceptance.map((a, i) => <li key={i}>{a}</li>)}
            </ul>
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <span className="lex-chip" data-size="sm">{r.category}</span>
            <span className="lex-chip" data-size="sm">{r.area}</span>
            {r.source.map((s) => <span key={s} className="lex-chip lex-chip--outlined" data-size="sm">{s}</span>)}
          </div>
        </div>
      )}
    </div>
  );
}

function ChipSelect({ label, value, options, onChange }: {
  label: string;
  value: string;
  options: { v: string; l?: string }[];
  onChange: (v: string) => void;
}) {
  return (
    <label style={{ display: "inline-flex", alignItems: "center", gap: 6, font: "var(--t-label-md)", color: "var(--md-on-surface-var)" }}>
      <span className="sr-only">{label}</span>
      <select
        aria-label={label}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{
          height: 32, padding: "0 10px",
          borderRadius: "var(--r-pill)", border: "1px solid var(--md-outline-var)",
          background: "var(--md-sc-lowest)", color: "var(--md-on-surface)",
          font: "var(--t-label-md)",
        }}
      >
        {options.map((o) => <option key={o.v} value={o.v}>{o.l ?? o.v}</option>)}
      </select>
    </label>
  );
}

export default AboutRequirements;
